import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { agentService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { Button, Panel } from '../../../atoms'
import { AgentNav } from '../../../organisms'

declare var window: interfaces.IWindow

type CrudActionKey = 'can_read' | 'can_create' | 'can_update' | 'can_delete'

interface IRoleData {
  id: number
  name: string
}

interface IRolePermissionData {
  id: number | null
  agent_role_id: number
  can_read: boolean
  can_create: boolean
  can_update: boolean
  can_delete: boolean
}

interface IPermissionData {
  agent_permission_id: number
  group: string
  name: string
  description: string
  roles: IRolePermissionData[]
}

interface IProps {
  user: any
}

const CRUD_ACTIONS = [
  { key: 'can_read', label: '閲覧' },
  { key: 'can_create', label: '作成' },
  { key: 'can_update', label: '更新' },
  { key: 'can_delete', label: '削除' },
]

const PermissionsMatrixAll: React.FC<IProps> = () => {
  const [roles, setRoles] = React.useState<IRoleData[]>([])
  const [permissions, setPermissions] = React.useState<IPermissionData[]>([])
  const [loading, setLoading] = React.useState(true)
  const [isTransposed, setIsTransposed] = React.useState(false)

  React.useEffect(() => {
    const load = async () => {
      try {
        const data = await agentService.getAllRolesPermissionMatrix()
        setRoles(data.roles)
        setPermissions(data.permissions)
      } catch (e) {
        console.error('権限マトリクスの読み込みに失敗しました', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // グループ別にパーミッションをまとめる
  const groupedPermissions = React.useMemo(() => {
    const groups: { [key: string]: IPermissionData[] } = {}
    permissions.forEach(p => {
      if (!groups[p.group]) {
        groups[p.group] = []
      }
      groups[p.group].push(p)
    })
    return groups
  }, [permissions])

  // セルクリック時の権限更新処理（楽観的UI更新＋ロールバック）
  const handleToggle = React.useCallback(
    async (perm: IPermissionData, rp: IRolePermissionData, actionKey: CrudActionKey) => {
      const newValue = !rp[actionKey]
      const updatedRp: IRolePermissionData = { ...rp, [actionKey]: newValue }

      // 楽観的UI更新
      setPermissions(prev =>
        prev.map(p =>
          p.agent_permission_id === perm.agent_permission_id
            ? {
                ...p,
                roles: p.roles.map(r => (r.agent_role_id === rp.agent_role_id ? updatedRp : r)),
              }
            : p
        )
      )

      try {
        const { role_permission } = await agentService.updateRolePermission({
          id: rp.id,
          agent_role_id: rp.agent_role_id,
          agent_permission_id: perm.agent_permission_id,
          can_read: updatedRp.can_read,
          can_create: updatedRp.can_create,
          can_update: updatedRp.can_update,
          can_delete: updatedRp.can_delete,
        })

        // 新規作成時のみサーバーから返却されたidを反映
        // 既存レコードの更新時はidを上書きせず、連打時のレースで古いレスポンスが最新state を上書きしないようにする
        if (rp.id === null && role_permission?.id) {
          setPermissions(prev =>
            prev.map(p =>
              p.agent_permission_id === perm.agent_permission_id
                ? {
                    ...p,
                    roles: p.roles.map(r =>
                      r.agent_role_id === rp.agent_role_id && r.id === null
                        ? { ...r, id: role_permission.id }
                        : r
                    ),
                  }
                : p
            )
          )
        }

        window.flashMessages.addMessage({
          text: '権限を更新しました',
          type: 'success',
        })
      } catch (e) {
        // ロールバック
        setPermissions(prev =>
          prev.map(p =>
            p.agent_permission_id === perm.agent_permission_id
              ? {
                  ...p,
                  roles: p.roles.map(r => (r.agent_role_id === rp.agent_role_id ? rp : r)),
                }
              : p
          )
        )
        window.flashMessages.addMessage({
          text: '権限の更新に失敗しました',
          type: 'error',
        })
      }
    },
    []
  )

  const renderCell = (
    perm: IPermissionData,
    rp: IRolePermissionData | undefined,
    actionKey: CrudActionKey
  ) => {
    const active = rp ? rp[actionKey] : false
    return (
      <ToggleCell
        type="button"
        active={active}
        disabled={!rp}
        onClick={() => rp && handleToggle(perm, rp, actionKey)}
        title={active ? 'ON' : 'OFF'}
      >
        {active ? '○' : '×'}
      </ToggleCell>
    )
  }

  // デフォルトビュー: 行=パーミッション、列=ロール
  const renderDefaultView = () => (
    <PermissionTable>
      <thead>
        <tr>
          <th className="sticky-col group-col">{I18n.t('generic.group')}</th>
          <th className="sticky-col name-col">{I18n.t('generic.name')}</th>
          {roles.map(role => (
            <th key={role.id} className="role-header" colSpan={4}>
              {role.name}
            </th>
          ))}
        </tr>
        <tr>
          <th className="sticky-col group-col" />
          <th className="sticky-col name-col" />
          {roles.map(role =>
            CRUD_ACTIONS.map(action => (
              <th key={`${role.id}-${action.key}`} className="action-col">
                {action.label}
              </th>
            ))
          )}
        </tr>
      </thead>
      <tbody>
        {Object.entries(groupedPermissions).map(([group, perms]) =>
          perms.map((perm, idx) => (
            <tr key={perm.agent_permission_id}>
              {idx === 0 && (
                <td className="sticky-col group-col" rowSpan={perms.length}>
                  {group}
                </td>
              )}
              <td className="sticky-col name-col">
                <span className="perm-name">{perm.name}</span>
                {perm.description && <span className="perm-desc">{perm.description}</span>}
              </td>
              {roles.map(role => {
                const rp = perm.roles.find(r => r.agent_role_id === role.id)
                return CRUD_ACTIONS.map(action => (
                  <td
                    key={`${role.id}-${perm.agent_permission_id}-${action.key}`}
                    className="action-col"
                  >
                    {renderCell(perm, rp, action.key as CrudActionKey)}
                  </td>
                ))
              })}
            </tr>
          ))
        )}
      </tbody>
    </PermissionTable>
  )

  // 転置ビュー: 行=ロール、列=パーミッション
  const renderTransposedView = () => (
    <PermissionTable>
      <thead>
        <tr>
          <th className="sticky-col role-name-col">ロール名</th>
          {permissions.map(perm => (
            <th key={perm.agent_permission_id} className="perm-header" colSpan={4}>
              <span className="perm-name">{perm.name}</span>
              {perm.description && <span className="perm-desc">{perm.description}</span>}
            </th>
          ))}
        </tr>
        <tr>
          <th className="sticky-col role-name-col" />
          {permissions.map(perm =>
            CRUD_ACTIONS.map(action => (
              <th key={`${perm.agent_permission_id}-${action.key}`} className="action-col">
                {action.label}
              </th>
            ))
          )}
        </tr>
      </thead>
      <tbody>
        {roles.map(role => (
          <tr key={role.id}>
            <td className="sticky-col role-name-col">{role.name}</td>
            {permissions.map(perm => {
              const rp = perm.roles.find(r => r.agent_role_id === role.id)
              return CRUD_ACTIONS.map(action => (
                <td
                  key={`${role.id}-${perm.agent_permission_id}-${action.key}`}
                  className="action-col"
                >
                  {renderCell(perm, rp, action.key as CrudActionKey)}
                </td>
              ))
            })}
          </tr>
        ))}
      </tbody>
    </PermissionTable>
  )

  return (
    <Wrapper>
      <AgentNav active="agent_roles" />
      <section className="Index_Main">
        <Panel title="権限マトリクス一覧">
          <ToolBar>
            <ToggleButton active={!isTransposed} onClick={() => setIsTransposed(false)}>
              パーミッション × ロール
            </ToggleButton>
            <ToggleButton active={isTransposed} onClick={() => setIsTransposed(true)}>
              ロール × パーミッション
            </ToggleButton>
          </ToolBar>

          {loading ? (
            <LoadingMessage>読み込み中...</LoadingMessage>
          ) : (
            <TableWrapper>
              {isTransposed ? renderTransposedView() : renderDefaultView()}
            </TableWrapper>
          )}

          <BackLink>
            <Button>
              <a href="/agent/agent_roles">{I18n.t('generic.back_to_index')}</a>
            </Button>
          </BackLink>
        </Panel>
      </section>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  min-height: calc(100vh - ${constants.HEADER_HEIGHT + constants.FOOTER_HEIGHT}px);
  margin: 0 auto;
  background-color: ${constants.COLORS.background};
  @media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    display: block;
    height: auto;
    min-height: calc(100vh - ${constants.FOOTER_HEIGHT}px);
    padding-top: 55px;
  }

  .Index_Main {
    width: 100%;
    height: 100%;
    max-width: none;
    min-width: 0;
    margin: 0 auto;
    padding: 22px 40px 80px 46px;

    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      padding: 16px 12px;
      margin: 0 auto;
    }
  }

  .Panel {
    max-width: none;
  }
`

const ToolBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
`

const ToggleButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${p => (p.active ? constants.COLORS.primary : '#ccc')};
  border-radius: 20px;
  background: ${p => (p.active ? constants.COLORS.primary : '#fff')};
  color: ${p => (p.active ? '#fff' : '#666')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    opacity: 0.8;
  }
`

const TableWrapper = styled.div`
  overflow-x: auto;
  max-width: 100%;
`

const PermissionTable = styled.table`
  border-collapse: collapse;
  font-size: 13px;
  white-space: nowrap;

  th,
  td {
    padding: 6px 8px;
    border: 1px solid #e0e0e0;
    text-align: center;
  }

  th {
    background: #f5f5f5;
    font-weight: 600;
  }

  .sticky-col {
    position: sticky;
    z-index: 1;
    background: #fafafa;
  }

  .group-col {
    left: 0;
    min-width: 100px;
    font-weight: 600;
    text-align: left;
  }

  .name-col {
    left: 100px;
    text-align: left;
    min-width: 140px;

    .perm-name {
      display: block;
      font-weight: 500;
    }

    .perm-desc {
      display: block;
      font-size: 11px;
      color: #888;
    }
  }

  .role-name-col {
    left: 0;
    min-width: 140px;
    font-weight: 600;
    text-align: left;
  }

  .role-header {
    min-width: 200px;
    background: #eef6ee;
  }

  .perm-header {
    min-width: 200px;
    background: #eef6ee;
    text-align: left;

    .perm-name {
      display: block;
      font-weight: 500;
    }

    .perm-desc {
      display: block;
      font-size: 11px;
      color: #888;
      font-weight: normal;
    }
  }

  .action-col {
    width: 40px;
    min-width: 40px;
  }
`

const ToggleCell = styled.button<{ active: boolean }>`
  display: inline-block;
  width: 28px;
  height: 28px;
  line-height: 1;
  text-align: center;
  border: 1px solid ${p => (p.active ? constants.COLORS.primary : '#ccc')};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  background: ${p => (p.active ? constants.COLORS.primary : '#fff')};
  color: ${p => (p.active ? '#fff' : '#999')};
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    cursor: default;
  }
`

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
`

const BackLink = styled.div`
  margin-top: 24px;
`

export default PermissionsMatrixAll
